/** 策略回测/模拟盘：类型 + 双语文案 */
import { api } from './api';

export interface I18nTxt {
  en: string;
  zhHant: string;
    zhHans?: string;
}

export interface TemplateParamDef {
  key: string;
  type: 'number' | 'select' | 'boolean';
  label: I18nTxt;
  default: number | string | boolean;
  options?: Array<{ value: string; label: I18nTxt }>;
  min?: number;
  max?: number;
  step?: number;
  advanced?: boolean;
}

export interface TemplateDto {
  key: string;
  version: number;
  name: I18nTxt;
  risk: 'low' | 'mid' | 'high' | 'max';
  type: string;
  interval: string;
  useFutures: boolean;
  marketType: 'spot' | 'futures';
  warmupBars: number;
  riskDisclosure: I18nTxt;
  defaultSymbol: string;
  supportedSymbols: string[];
  shortDesc: I18nTxt;
  logic: I18nTxt;
  fit: I18nTxt;
  unfit: I18nTxt;
  simpleParams: TemplateParamDef[];
  advancedParams: TemplateParamDef[];
  portfolioMode?: boolean;
  isActive: boolean;
  preview: BacktestOutput | null;
}

export interface EquityPoint { t: number; equity: number; cash?: number; drawdown?: number; }

export interface BacktestOutput {
  equityCurve: EquityPoint[];
  benchmarkCurve: EquityPoint[];
  metrics: {
    totalReturnPct: number;
    annualizedPct: number;
    maxDrawdownPct: number;
    winRatePct: number;
    profitFactor: number;
    tradeCount: number;
    finalEquity: number;
    benchmarkReturnPct: number;
    winRateStatus: 'valid' | 'not_applicable' | 'insufficient_data';
    profitFactorStatus: 'valid' | 'not_applicable' | 'no_losses';
    investedUsdt: number | null;
    remainingCash: number | null;
    holdingQty: number | null;
    avgCost: number | null;
    finalPositionValue: number | null;
    rulesChecked: number;
    ruleFailures: { reason: string; count: number }[];
    idleCashRatio: number | null;
  };
  trades: Array<{ time: number; side: string; price: number; qty: number; pnl: number | null; reason: string; floatingPnl?: number | null }>;
  skips: Array<{ time: number; reason: string }>;
  summary: I18nTxt;
  barsUsed: number;
  monthly: Array<{ ym: string; equityEnd: number; retPct: number; benchmarkRetPct: number }>;
  dataCoverage: { expectedBars: number; actualBars: number; missingRanges: { startMs: number; endMs: number }[] };
  /** A1/A2 定投基准（同计划、同预算、同费用的机械定投，文档 V2.0 P143/P485） */
  dcaCurve?: EquityPoint[];
  dcaReturnPct?: number;
  generatedAt: number;
  /** 阶段D/GRID：网格模板明细（价格线数、保守跨层计数、已实现盈亏、库存估值） */
  gridInfo?: {
    levels: number;
    priceLines: number;
    ambiguousBars: number;
    realizedPnl: number;
    inventoryQty: number;
    inventoryValue: number;
  };
  /** 阶段E/STR08：按环境（日K ADX14+SMA60 规则）分段的统计与按入场环境胜率 */
  regimes?: {
    definition: { en: string; zhHant: string };
    byEnv: Array<{ env: 'up' | 'down' | 'sideways'; days: number; retContributionPct: number; dailyCostUsdt: number }>;
    byEntry: Array<{ env: 'up' | 'down' | 'sideways' | 'unknown'; trades: number; winRatePct: number; status: 'valid' | 'not_applicable' | 'insufficient_data' }>;
    observed: boolean;
    insufficientEnvs: string[];
  };
}

/** STR02 预检结果 */
export interface PreflightResult {
  ok: boolean;
  templateKey: string;
  version: number;
  symbol: string;
  marketType: 'spot' | 'futures';
  interval: string;
  requestedStartMs: number;
  requestedEndMs: number;
  capital: number;
  availableStartMs: number | null;
  availableEndMs: number | null;
  warmupOk: boolean;
  expectedBars: number;
  actualBars: number;
  missingRanges: { startMs: number; endMs: number }[];
  feeAssumption: { mode: string; detail: string } | null;
  markPriceAssumption: { mode: string } | null;
  /** F1 合约数据预检：合约乘数/最小下单/保证金阶梯（仅合约模式；获取失败为 blocking） */
  contractSpecs?: {
    multiplier: number;
    tickSize: number;
    stepSize: number;
    minNotional: number;
    leverageTiers: Array<{ tier: number; initialLeverage: number; maxNotionalUsdt: number; maintMarginRatio: number }>;
  } | null;
  warnings: { code: string; en: string; zhHant: string }[];
  blockingErrors: { code: string; en: string; zhHant: string }[];
  preflightToken: string;
  configHash: string;
  expiresInSec: number;
}

export interface BacktestJob {
  id: string;
  state: 'queued' | 'running' | 'succeeded' | 'failed' | 'cancelled';
  result: BacktestOutput | null;
  error: { message?: string } | null;
  templateKey?: string;
  symbol?: string;
  interval?: string;
  startMs?: number;
  endMs?: number;
  params?: Record<string, unknown>;
  inputParams?: Record<string, unknown> | null;
  capital?: number;
  configHash?: string | null;
  templateVersion?: number | null;
}

/** S04 回测任务页：最近回测记录条目 */
export interface RecentBacktestItem {
  id: string;
  templateKey: string;
  symbol: string | null;
  interval: string;
  state: 'queued' | 'running' | 'succeeded' | 'failed' | 'cancelled';
  capital: number;
  createdAt: string;
  totalReturnPct: number | null;
  benchmarkReturnPct: number | null;
}

export interface PaperOrder {
  id: string;
  paperId: string;
  time: number;
  side: string;
  price: number;
  qty: number;
  pnl: number | null;
  reason: string | null;
  fee?: number | null;
  type?: string;
  status?: string;
}

/** STR07 证据时间线：单次规则评估快照 */
export interface PaperEvaluation {
  id: string;
  paperId: string;
  evaluationId: string;
  strategyVersion: string | null;
  asOf: number;
  inputSnapshot: Record<string, any> | null;
  conditions: Array<{ name: string; value: number | string; operator: string; threshold: number | string | null; passed: boolean }> | null;
  decision: 'BUY' | 'SELL' | 'HOLD';
  reasonCode: string | null;
  barOpenTime: number | null;
  availableAt: number | null;
  signaled: boolean;
}

/** STR07 按日聚合：未交易原因分布 */
export interface EvalStats {
  total: number;
  byDay: Array<{ day: string; checks: number; byReason: Record<string, number> }>;
}

/** STR07 未交易原因 → 双语标签 */
export function evalReasonLabel(zh: boolean, code: string | null): string {
  if (!code || code === 'EXECUTED') return zh ? '已成交' : 'Executed';
  const map: Record<string, { en: string; zhHant: string }> = {
    NO_SIGNAL: { en: 'No signal', zhHant: '无信号' },
    TREND_FILTER_FAILED: { en: 'Trend filter failed', zhHant: '趋势过滤未通过' },
    INSUFFICIENT_CASH: { en: 'Insufficient cash', zhHant: '资金不足' },
    ALREADY_POSITIONED: { en: 'Already positioned', zhHant: '已有持仓' },
    DATA_STALE: { en: 'Stale data', zhHant: '数据延迟' },
    MIN_NOTIONAL: { en: 'Below min notional', zhHant: '未达最小下单额' },
    RISK_BLOCKED: { en: 'Risk blocked', zhHant: '风控阻断' },
    PAUSED: { en: 'Paused', zhHant: '已暂停' },
  };
  const m = map[code];
  if (!m) return code;
  return zh ? m.zhHant : m.en;
}

/** 逐笔交易 reason 双语：基于关键词匹配，保留数值/层级后缀；未命中回退原串。 */
export function tradeReasonLabel(zh: boolean, reason: string | null | undefined): string {
  if (!reason) return '—';
  const rules: Array<{ pat: RegExp; en: string; zhHant: string }> = [
    { pat: /^scheduled buy$/i, en: 'Scheduled buy', zhHant: '定期买入' },
    { pat: /^dip double-up$/i, en: 'Dip double-up', zhHant: '回调加码' },
    { pat: /^grid initial position/i, en: 'Grid initial position', zhHant: '网格初始建仓' },
    { pat: /^range exit sell L(\d+)$/i, en: 'Range exit sell', zhHant: '区间退出卖出' },
    { pat: /^range exit initial position$/i, en: 'Range exit initial', zhHant: '区间退出初始仓' },
    { pat: /^grid level (\d+)$/i, en: 'Grid level buy', zhHant: '网格层买入' },
    { pat: /^grid take-profit (\d+)$/i, en: 'Grid take-profit', zhHant: '网格止盈' },
    { pat: /^RSI (\S+) < (\S+)$/i, en: 'RSI oversold buy', zhHant: 'RSI 超卖买入' },
    { pat: /^RSI (\S+) > (\S+)$/i, en: 'RSI overbought sell', zhHant: 'RSI 超买卖出' },
    { pat: /^MA golden cross$/i, en: 'MA golden cross', zhHant: '均线金叉' },
    { pat: /^MA death cross$/i, en: 'MA death cross', zhHant: '均线死叉' },
    { pat: /^breakout above (\S+)d high$/i, en: 'Breakout above high', zhHant: '突破创高买入' },
    { pat: /^stop below (\S+)d low$/i, en: 'Stop below low', zhHant: '跌破支撑止损' },
    { pat: /^flip to long$/i, en: 'Flip to long', zhHant: '反手做多' },
    { pat: /^flip to short$/i, en: 'Flip to short', zhHant: '反手做空' },
    { pat: /^open long/i, en: 'Open long', zhHant: '开多仓' },
    { pat: /^open short/i, en: 'Open short', zhHant: '开空仓' },
    { pat: /^close long/i, en: 'Close long', zhHant: '平多仓' },
    { pat: /^close short/i, en: 'Close short', zhHant: '平空仓' },
    { pat: /liquidation/i, en: 'Liquidation', zhHant: '强平' },
    { pat: /^archive flat \(stop\)$/i, en: 'Archive flat (stop)', zhHant: '存档平仓（停损）' },
    // V2.0 批次2/4：T1/R1/A1 + T2/T3/T4/R2/R3
    { pat: /^T1 breakout > \d+ bar high$/i, en: 'Trend breakout', zhHant: 'T1 通道突破' },
    { pat: /^T1 channel exit$/i, en: 'Channel exit', zhHant: '通道退出' },
    { pat: /^T1 trend exit$/i, en: 'Trend exit', zhHant: '趋势退出' },
    { pat: /^T1 stop$/i, en: 'Stop loss', zhHant: '停损' },
    { pat: /^R1 reversion \(RSI \d+\)$/i, en: 'Range reversion', zhHant: '震荡回归买入' },
    { pat: /^R1 stop loss @ /i, en: 'Stop loss', zhHant: 'R1 停损' },
    { pat: /^R1 mean-revert exit$/i, en: 'Mean-revert exit', zhHant: '均线回归离场' },
    { pat: /^R1 env left RANGE$/i, en: 'Regime left range', zhHant: '离开震荡环境' },
    { pat: /^A1 accumulation #\d+$/i, en: 'Planned accumulation', zhHant: '定期累积' },
    { pat: /^T2 EMA\d+\/\d+ cross up$/i, en: 'EMA golden cross', zhHant: 'T2 均线金叉' },
    { pat: /^T2 trailing stop$/i, en: 'Trailing stop', zhHant: '移动停损' },
    { pat: /^T2 trend exit$/i, en: 'Trend exit', zhHant: '趋势退出' },
    { pat: /^T3 pullback confirmed$/i, en: 'Pullback confirmed', zhHant: 'T3 回踩确认' },
    { pat: /^T3 stop$/i, en: 'Stop loss', zhHant: '停损' },
    { pat: /^T3 pullback trend exit$/i, en: 'Trend exit', zhHant: '趋势退出' },
    { pat: /^T4 squeeze breakout$/i, en: 'Squeeze breakout', zhHant: 'T4 波动收缩突破' },
    { pat: /^T4 stop$/i, en: 'Stop loss', zhHant: '停损' },
    { pat: /^T4 breakdown exit$/i, en: 'Breakdown exit', zhHant: '跌破离场' },
    { pat: /^R2 RSI\d+ oversold cross$/i, en: 'RSI oversold cross', zhHant: 'R2 RSI 超卖上穿' },
    { pat: /^R2 stop$/i, en: 'Stop loss', zhHant: '停损' },
    { pat: /^R2 RSI\/regime exit$/i, en: 'RSI/regime exit', zhHant: 'RSI/环境离场' },
    { pat: /^R3 Z\d+ reversion$/i, en: 'Z-score reversion', zhHant: 'R3 Z 值回归' },
    { pat: /^R3 stop$/i, en: 'Stop loss', zhHant: '停损' },
    { pat: /^R3 Z\/regime exit$/i, en: 'Z/regime exit', zhHant: 'Z/环境离场' },
  ];
  for (const r of rules) {
    if (r.pat.test(reason)) return zh ? r.zhHant : r.en;
  }
  return reason;
}

export type PaperState =
  | 'created'
  | 'running'
  | 'paused'
  | 'stopping'
  | 'stopped'
  | 'data_stale'
  | 'liquidated'
  | 'risk_stopped'
  | 'error';

export interface PaperAccount {
  id: string;
  templateKey: string;
  symbol: string;
  interval: string;
  params: Record<string, any>;
  initialCapital: number;
  state: PaperState;
  equitySnapshot: EquityPoint[] | null;
  currentEquity: number;
  returnPct: number;
  stopReason: string | null;
  stateVersion: number;
  name: string | null;
  configHash: string | null;
  sourcePaperId: string | null;
  lastEvalAt: number | null;
  nextEvalAt: number | null;
  dataStaleAt: number | null;
  errorMsg: string | null;
  createdAt: string;
  position: { dir: number; posQty: number; positionUsdt: number; entry: number; cash: number };
  liquidationPrice: number | null;
  leverage: number;
  // S09 运行详情派生指标
  unrealizedPnl?: number;
  realizedPnl?: number;
  riskUsedPct?: number;
}

export interface StrategyCopy {
  title: string;
  subtitle: string;
  myPapers: string;
  runBacktest: string;
  startPaper: string;
  advanced: string;
  advancedHint: string;
  backtestResult: string;
  running: string;
  queued: string;
  failed: string;
  succeeded: string;
  loginRequired: string;
  riskLow: string;
  riskMid: string;
  riskHigh: string;
  riskMax: string;
  fitFor: string;
  watchOut: string;
  logic: string;
  symbol: string;
  // S01 策略中心（对齐文档示意图）
  labTagline: string;
  tabTemplates: string;
  tabMySims: string;
  tabBacktests: string;
  filterUse: string;
  useAll: string;
  useTrend: string;
  useReversion: string;
  useGrid: string;
  useAccum: string;
  useMulti: string;
  filterMarket: string;
  filterSymbol: string;
  viewRules: string;
  researchStatus: string;
  moreModules: string;
  moduleP1: string;
  moduleP1Desc: string;
  moduleP1Link: string;
  moduleF1: string;
  moduleF1Desc: string;
  moduleF1Link: string;
  twelveLabel: string;
  twelveDetail: string;
  viewResearchMethod: string;
  noBacktests: string;
  open: string;
  btTemplate: string;
  btSymbol: string;
  btState: string;
  btReturn: string;
  btTime: string;
  sharedFunds: string;
  sharedFundsDesc: string;
  portfolioCoins: string;
  initialCapital: string;
  backtestPeriod: string;
  period90: string;
  period180: string;
  period360: string;
  marketType: string;
  leverageNote: string;
  amountUsdt: string;
  frequency: string;
  riskLevel: string;
  totalReturn: string;
  vsBtc: string;
  maxDrawdown: string;
  winRate: string;
  profitFactor: string;
  trades: string;
  benchmark: string;
  strategyEquity: string;
  dcaBenchmark: string;
  dcaReturn: string;
  paperEquity: string;
  currentEquity: string;
  returnPct: string;
  status: string;
  stCreated: string;
  stRunning: string;
  stPaused: string;
  stStopped: string;
  start: string;
  pause: string;
  resume: string;
  stop: string;
  orders: string;
  position: string;
  openLong: string;
  openShort: string;
  flat: string;
  liqPrice: string;
  leverage: string;
  cash: string;
  noPapers: string;
  noOrders: string;
  stoppedReason: string;
  circuitBreaker: string;
  userStop: string;
  backtestHint: string;
  paperHint: string;
  preview: string;
  loading: string;
  back: string;
  strategyCenter: string;
  futuresBadge: string;
  spotBadge: string;
  notApplicable: string;
  noLossSamples: string;
  smallSample: string;
  configChanged: string;
  reportRange: string;
  market: string;
  paramsUsed: string;
  generatedAt: string;
  viewReport: string;
  lastValuation: string;
  waitingFirstEval: string;
  viewOnly: string;
  tabOverview: string;
  tabConfig: string;
  tabReport: string;
  investedUsdt: string;
  remainingCash: string;
  holdingQty: string;
  avgCost: string;
  finalPositionValue: string;
  versionLabel: string;
  costAssumption: string;
  skippedBuys: string;
  sampleThrough: string;
  netPnL: string;
  samples: string;
  returnSources: string;
  strategyReturn: string;
  benchmarkReturn: string;
  excessReturn: string;
  monthlyPerf: string;
  month: string;
  monthReturn: string;
  tradeLog: string;
  dataQuality: string;
  coverageBars: string;
  gaps: string;
  ruleChecks: string;
  ruleFailures: string;
  idleCash: string;
  preflightTitle: string;
  preflightOk: string;
  preflightBlocked: string;
  preflightWarn: string;
  preflightConfirm: string;
  preflightCancel: string;
  availWindow: string;
  warmupOkLabel: string;
  warmupNoLabel: string;
  runAgain: string;
  stStopping: string;
  stStale: string;
  stLiquidated: string;
  stRiskStopped: string;
  stError: string;
  filterAll: string;
  filterRunning: string;
  filterPaused: string;
  filterAbnormal: string;
  filterEnded: string;
  lastEval: string;
  nextEval: string;
  noSignalYet: string;
  copyCreate: string;
  stopConfirmTitle: string;
  stopConfirmMsg: string;
  confirm: string;
  cancel: string;
  pausedHint: string;
  stoppingHint: string;
  dataStaleHint: string;
  errorHint: string;
  liquidatedHint: string;
  riskStoppedHint: string;
  clonedFrom: string;
  decisionLog: string;
  configSnapshot: string;
  tradeType: string;
  evalTitle: string;
  evalTime: string;
  evalDecision: string;
  evalReason: string;
  evalConditions: string;
  evalInput: string;
  evalIndicator: string;
  loadMore: string;
  noEvaluations: string;
  evalChecks: string;
  dayLabel: string;
  checksLabel: string;
  envTitle: string;
  envChartTitle: string;
  envCurrent: string;
  envUtc: string;
  envEr20: string;
  envStreak: string;
  envDir: string;
  envVolPct: string;
  envCaption: string;
  envUnavailable: string;
  useTemplate: string;
  transparentRules: string;
  ruleEntry: string;
  ruleExit: string;
  ruleRisk: string;
  ruleExpand: string;
  ruleExpandLabel: string;
  ruleFull: string;
  risksToKnow: string;
  riskFreqStop: string;
  riskGap: string;
  riskNewAlgo: string;
  // S09 模拟运行详情 + S10 决策证据
  simDetail: string;
  evidenceTitle: string;
  equityUsdt: string;
  unrealizedPnl: string;
  realizedPnl: string;
  openPlanRisk: string;
  strategySolid: string;
  benchmarkDashed: string;
  marketUpdated: string;
  pauseNew: string;
  stopAndClose: string;
  currentFlat: string;
  holdingPos: string;
  waitSignal: string;
  tabPositions: string;
  tabEvents: string;
  tabReview: string;
  tabBudget: string;
  tabForwardReplay: string;
  viewEvidence: string;
  viewTraceEvidence: string;
  viewMetricsEvidence: string;
  noOrderThisTime: string;
  orderThisTime: string;
  reasonLabel: string;
  execResult: string;
  noOrderGenerated: string;
  cashUnchanged: string;
  noFees: string;
  auditTrail: string;
  snapshotSaved: string;
  versionFrozen: string;
  expandRaw: string;
  condName: string;
  condValue: string;
  condRequire: string;
  condResult: string;
  condPassed: string;
  condFailed: string;
  evidenceNote: string;
  rulesCheck: string;
  // S11 研究验证
  researchTitle: string;
  researchNoConclusion: string;
  researchInProgress: string;
  moduleRolling: string;
  moduleHoldout: string;
  moduleCost: string;
  moduleForward: string;
  pendingLabel: string;
  notStartedLabel: string;
  viewProtocol: string;
  publishCheck: string;
  pubSample: string;
  pubDrawdown: string;
  pubForward: string;
  pubGateMobile: string;
  pubKeepResearch: string;
  viewAllTrials: string;
  avoidOverfit: string;
  avoidCandidates: string;
  avoidSelection: string;
  avoidNoPromise: string;
  adminSubmitOnly: string;
  // S12 启动确认
  startConfirmTitle: string;
  startConfirmSub: string;
  preLaunchCheck: string;
  riskSummary: string;
  perTradeRisk: string;
  dailyLossPause: string;
  accountDDEnd: string;
  researchNotPass: string;
  virtualOnly: string;
  virtualOnlyDesc: string;
  confirmStartVirtual: string;
  iUnderstand: string;
  configLocked: string;
  backToConfig: string;
  dataPreflightOk: string;
  dataPreflightFail: string;
  startLockedHint: string;
  execMinute: string;
  feeAndSlippage: string;
  configHashLabel: string;
  // S13 币池选择
  universeTitle: string;
  universeSub: string;
  universeSearchPh: string;
  tabManual: string;
  tabDynamic: string;
  tabSaved: string;
  colMarket: string;
  colCoverage: string;
  colEligible: string;
  selectedCount: string;
  universeTypeStatic: string;
  universeTypeDynamic: string;
  lockMembersOnSave: string;
  checkConfirmUniverse: string;
  eligibleYes: string;
  eligibleNo: string;
  coverageFull: string;
  coveragePartial: string;
  coverageNone: string;
  eligibilityNote: string;
  selectedCoin: string;
  unselectedCoin: string;
  historyFullEligible: string;
  warmupInsufficient: string;
  viewReason: string;
  searchNoResult: string;
  confirmSearch: string;
  onlyVirtualFunds: string;
  universeSavedOk: string;
  universeDeleteOk: string;
  universeEmpty: string;
  savedUniverseName: string;
  dynamicRuleLabel: string;
  dynamicRuleDesc: string;
  dynamicTakeN: string;
  dynamicSortBy: string;
  backToStrategy: string;
  // S14 批量回测
  batchResultTitle: string;
  batchResultSub: string;
  independentBatch: string;
  perCoinBudget: string;
  summaryLine: string;
  colCoin: string;
  colNetReturn: string;
  colClosedTrades: string;
  colState: string;
  stDone: string;
  stDataInsufficient: string;
  stFailed: string;
  stPending: string;
  completenessTitle: string;
  completenessProgress: string;
  completenessNote: string;
  failedNotRanked: string;
  independentAccounts: string;
  sharedNeedsNewTask: string;
  viewDistribution: string;
  mobileSummary: string;
  mobileIncompleteKept: string;
  incompleteNote: string;
  viewPerCoinReports: string;
  notSumToShared: string;
  medianReturn: string;
  sameWindowCost: string;
  sortNetReturn: string;
  sampleResults: string;
  batchQueued: string;
  batchRunning: string;
  startBatch: string;
  emptyBatchHint: string;
  // S14 共享批量（SHARED，文档 P202/P211）
  sharedMode: string;
  independentMode: string;
  sharedBatch: string;
  sharedBatchNote: string;
  portfolioReturn: string;
  portfolioBenchmark: string;
  contributionUsdtHeader: string;
  viewPortfolioAttribution: string;
  sharedResultPending: string;
  unitNoteShared: string;
  // ---- S15 组合归因 ----
  analysisTitle: string;
  analysisSubtitle: string;
  analysisMobileSub: string;
  portfolioEquity: string;
  portfolioMaxDrawdown: string;
  totalNetPnl: string;
  maxHoldings: string;
  portfolioEquityUsdt: string;
  strategySolidBaseDashed: string;
  sharedRiskLimits: string;
  singleCoinLimit: string;
  totalExposureLimit: string;
  openRiskLimit: string;
  concurrentIntentApproval: string;
  contributionUsdt: string;
  exposure: string;
  holdings: string;
  s15Trades: string;
  correlation: string;
  totalInclFees: string;
  contributionSumNotPct: string;
  mobileRiskLine: string;
  netPnlContribution: string;
  unitNote: string;
  // ---- S16 信号分析 ----
  signalAnalysisTitle: string;
  validIntents: string;
  executed: string;
  rejected: string;
  execRate: string;
  rejectReasonDist: string;
  filterScope: string;
  filterTemplate: string;
  signalFilterSymbol: string;
  filterTime: string;
  filterReasonStatus: string;
  clickForEvidence: string;
  signalEvidenceNote: string;
  ruleNotTriggeredNote: string;
  signalMarket: string;
  result: string;
  mainReason: string;
  action: string;
  signalViewEvidence: string;
  signalViewReport: string;
  detailRows: string;
  clickForDetail: string;
  chooseTask: string;
  chooseTaskHint: string;
  singleBacktests: string;
  sharedBacktests: string;
  noTasks: string;
  noTaskHint: string;
  noIntents: string;
  noIntentsNote: string;
  allRejectedNote: string;
  taskScope: string;
  execState: string;
  intentDetailTitle: string;
  reasonNone: string;
}

export function strategyCopy(zh: boolean): StrategyCopy {
  return {
    title: zh ? '策略中心' : 'Strategy Center',
    subtitle: zh ? '选一个策略模板，看它过去怎么赚钱，再一键开模拟盘试跑。全程不用懂任何专业术语。' : 'Pick a template, see how it performed in the past, then start a paper account with one click. No jargon needed.',
    myPapers: zh ? '我的模拟盘' : 'My Paper Accounts',
    runBacktest: zh ? '运行回测' : 'Run Backtest',
    startPaper: zh ? '开始模拟盘' : 'Start Paper',
    advanced: zh ? '高级参数' : 'Advanced',
    advancedHint: zh ? '（可展开，预设已给合理值）' : '(expandable, sensible defaults preset)',
    backtestResult: zh ? '回测结果' : 'Backtest Result',
    running: zh ? '计算中…' : 'Running…',
    queued: zh ? '排队中…' : 'Queued…',
    failed: zh ? '回测失败' : 'Backtest failed',
    succeeded: zh ? '已完成' : 'Done',
    loginRequired: zh ? '请先登入后使用回测与模拟盘' : 'Sign in to use backtest & paper trading',
    riskLow: zh ? '稳健' : 'Low risk',
    riskMid: zh ? '平衡' : 'Medium risk',
    riskHigh: zh ? '进取' : 'High risk',
    riskMax: zh ? '高风险' : 'Max risk',
    fitFor: zh ? '适合' : 'Good for',
    watchOut: zh ? '注意' : 'Watch out',
    logic: zh ? '策略逻辑' : 'Strategy logic',
    symbol: zh ? '币种' : 'Symbol',
    labTagline: zh ? '策略实验室 · 透明规则 · 历史验证 · 模拟观察' : 'Strategy Lab · Transparent rules · Verified history · Paper observation',
    tabTemplates: zh ? '模板库' : 'Templates',
    tabMySims: zh ? '我的模拟' : 'My Papers',
    tabBacktests: zh ? '回测记录' : 'Backtests',
    filterUse: zh ? '用途' : 'Use',
    useAll: zh ? '全部' : 'All',
    useTrend: zh ? '趋势' : 'Trend',
    useReversion: zh ? '回归' : 'Reversion',
    useGrid: zh ? '网格' : 'Grid',
    useAccum: zh ? '积累' : 'Accumulate',
    useMulti: zh ? '多币' : 'Multi',
    filterMarket: zh ? '市场' : 'Market',
    filterSymbol: zh ? '币对' : 'Symbol',
    viewRules: zh ? '查看规则' : 'View rules',
    researchStatus: zh ? '当前版本待历史验证' : 'Pending historical validation',
    moreModules: zh ? '更多研究模块' : 'More research modules',
    moduleP1: zh ? 'P1 风险平衡组合' : 'P1 Risk-balanced portfolio',
    moduleP1Desc: zh ? '在同一预算下统一控制风险，结果必须重新组合回测。' : 'One shared budget with unified risk control; results are re-run as a combined backtest.',
    moduleP1Link: zh ? '查看组合模板 →' : 'View portfolio templates →',
    moduleF1: zh ? 'F1 永续研究' : 'F1 Perpetual research',
    moduleF1Desc: zh ? '合约数据与清算模型已完备，T1 合约模式已开放双向做空。' : 'Futures data & liquidation model are ready — T1 futures mode is live with two-way trading.',
    moduleF1Link: zh ? '查看合约模式 →' : 'View futures mode →',
    twelveLabel: zh ? '12 类研究模板' : '12 research templates',
    twelveDetail: zh ? '趋势 4 类 · 回归 3 类 · 网格 1 类 · 积累 2 类 · 多币 2 类' : 'Trend 4 · Reversion 3 · Grid 1 · Accumulate 2 · Multi-symbol 2',
    viewResearchMethod: zh ? '查看研究方法' : 'View research method',
    noBacktests: zh ? '暂无回测记录，去模板库运行一次回测吧' : 'No backtest records yet — run one from the template library',
    open: zh ? '打开' : 'Open',
    btTemplate: zh ? '模板' : 'Template',
    btSymbol: zh ? '币种' : 'Symbol',
    btState: zh ? '状态' : 'Status',
    btReturn: zh ? '总收益' : 'Return',
    btTime: zh ? '时间' : 'Time',
    sharedFunds: zh ? '共享资金组合' : 'Shared fund portfolio',
    sharedFundsDesc: zh ? '此策略在一个共享资金池上同时运作多个币种（仅现货），资金在币种间分配并由组合风险规则约束。' : 'This strategy runs multiple symbols on one shared capital pool (spot only); funds are allocated across symbols under portfolio risk rules.',
    portfolioCoins: zh ? '参与币种（至少 2 个，最多 12 个）' : 'Symbols (2–12)',
    initialCapital: zh ? '初始资金 (USDT)' : 'Initial capital (USDT)',
    backtestPeriod: zh ? '回测周期' : 'Backtest period',
    period90: zh ? '90 天' : '90 days',
    period180: zh ? '180 天' : '180 days',
    period360: zh ? '360 天' : '360 days',
    marketType: zh ? '市场类型' : 'Market type',
    leverageNote: zh ? '合约模式：逐仓，默认 1 倍，风险档上限 2 倍；包含资金费与强平风险，名义仓位不超过权益 30%' : 'Futures: isolated margin, default 1×, up to 2× by risk level; funding & liquidation apply, notional capped at 30% of equity',
    amountUsdt: zh ? '投入金额' : 'Capital',
    frequency: zh ? '频率' : 'Frequency',
    riskLevel: zh ? '风险偏好' : 'Risk level',
    totalReturn: zh ? '总收益' : 'Total return',
    vsBtc: zh ? '同期买入持有' : 'Buy & hold',
    maxDrawdown: zh ? '最大回撤' : 'Max drawdown',
    winRate: zh ? '胜率' : 'Win rate',
    profitFactor: zh ? '盈亏比' : 'Profit factor',
    trades: zh ? '平仓交易' : 'Closed trades',
    benchmark: zh ? '基准' : 'Benchmark',
    strategyEquity: zh ? '策略权益' : 'Strategy equity',
    dcaBenchmark: zh ? '定投基准' : 'DCA benchmark',
    dcaReturn: zh ? '定投收益' : 'DCA return',
    paperEquity: zh ? '模拟盘权益曲线' : 'Paper equity curve',
    currentEquity: zh ? '当前净值' : 'Current equity',
    returnPct: zh ? '收益率' : 'Return',
    status: zh ? '状态' : 'Status',
    stCreated: zh ? '待启动' : 'Created',
    stRunning: zh ? '运行中' : 'Running',
    stPaused: zh ? '已暂停' : 'Paused',
    stStopped: zh ? '已终止' : 'Stopped',
    start: zh ? '启动' : 'Start',
    pause: zh ? '暂停' : 'Pause',
    resume: zh ? '继续' : 'Resume',
    stop: zh ? '终止' : 'Stop',
    orders: zh ? '订单与信号日志' : 'Orders & signal log',
    position: zh ? '当前持仓' : 'Position',
    openLong: zh ? '多单' : 'Long',
    openShort: zh ? '空单' : 'Short',
    flat: zh ? '空仓' : 'Flat',
    liqPrice: zh ? '强平线（模拟）' : 'Liquidation (paper)',
    leverage: zh ? '杠杆' : 'Leverage',
    cash: zh ? '可用资金' : 'Available cash',
    noPapers: zh ? '暂无模拟盘，先去策略中心选一个模板吧' : 'No paper accounts yet — pick a template in Strategy Center',
    noOrders: zh ? '暂无订单记录' : 'No orders yet',
    stoppedReason: zh ? '终止原因' : 'Stop reason',
    circuitBreaker: zh ? '回撤熔断' : 'Circuit breaker',
    userStop: zh ? '手动终止' : 'User stop',
    backtestHint: zh ? '回测区间为 2024-01 至今，主基准为同标的买入持有（BTC 可作辅助对比）。' : 'Backtest range: 2024-01 to now. Primary benchmark is buy-and-hold on the same market & symbol (BTC optional).',
    paperHint: zh ? '模拟盘按真实行情撮合，含手续费与滑点；合约模板展示强平线但不真爆仓。' : 'Paper trades match live prices with fees & slippage; futures templates show a liquidation line but never really liquidate.',
    preview: zh ? '近 1 年回测摘要' : '1Y backtest preview',
    loading: zh ? '载入中…' : 'Loading…',
    back: zh ? '返回' : 'Back',
    strategyCenter: zh ? '策略中心' : 'Strategy Center',
    futuresBadge: zh ? '合约' : 'Futures',
    spotBadge: zh ? '现货' : 'Spot',
    notApplicable: zh ? '不适用' : 'Not applicable',
    noLossSamples: zh ? '无亏损样本' : 'No loss samples',
    smallSample: zh ? '样本偏少' : 'Small sample',
    configChanged: zh ? '配置已改变，此为旧配置的结果' : 'Configuration changed — this is from an older setup',
    reportRange: zh ? '回测区间' : 'Backtest range',
    market: zh ? '市场' : 'Market',
    paramsUsed: zh ? '参数快照' : 'Parameter snapshot',
    generatedAt: zh ? '生成时间' : 'Generated',
    viewReport: zh ? '查看报告' : 'View report',
    lastValuation: zh ? '最近估值' : 'Last valuation',
    waitingFirstEval: zh ? '等待首次评估' : 'Waiting for first evaluation',
    viewOnly: zh ? '已结束，仅供查阅' : 'Ended — view only',
    tabOverview: zh ? '概览' : 'Overview',
    tabConfig: zh ? '配置' : 'Config',
    tabReport: zh ? '报告' : 'Report',
    investedUsdt: zh ? '累计投入' : 'Total invested',
    remainingCash: zh ? '剩余现金' : 'Remaining cash',
    holdingQty: zh ? '持仓数量' : 'Holding qty',
    avgCost: zh ? '平均含费成本' : 'Avg cost (incl. fees)',
    finalPositionValue: zh ? '期末持仓价值' : 'Final position value',
    versionLabel: zh ? '版本' : 'Version',
    costAssumption: zh ? '成本假设' : 'Cost assumption',
    skippedBuys: zh ? '预算不足跳过' : 'Skipped (insufficient cash)',
    sampleThrough: zh ? '样本截至' : 'Sample through',
    netPnL: zh ? '净盈亏（仅已实现）' : 'Net P&L (realized only)',
    samples: zh ? '样本数' : 'Samples',
    returnSources: zh ? '收益来源' : 'Return sources',
    strategyReturn: zh ? '策略收益' : 'Strategy',
    benchmarkReturn: zh ? '基准收益' : 'Benchmark',
    excessReturn: zh ? '超额收益' : 'Excess',
    monthlyPerf: zh ? '月度表现' : 'Monthly performance',
    month: zh ? '月份' : 'Month',
    monthReturn: zh ? '当月收益' : 'Return',
    tradeLog: zh ? '逐笔交易' : 'Trade log',
    dataQuality: zh ? '数据质量' : 'Data quality',
    coverageBars: zh ? 'K线覆盖' : 'Bars covered',
    gaps: zh ? '缺失区间' : 'Gaps',
    ruleChecks: zh ? '规则检查次数' : 'Rule checks',
    ruleFailures: zh ? '未通过原因分布' : 'Failure distribution',
    idleCash: zh ? '资金闲置比例' : 'Idle cash ratio',
    preflightTitle: zh ? '回测前预检' : 'Backtest preflight',
    preflightOk: zh ? '预检通过，可开始回测' : 'Preflight passed, ready to run',
    preflightBlocked: zh ? '存在阻塞项，无法开始回测' : 'Blocking issues found — cannot run',
    preflightWarn: zh ? '注意事项' : 'Notes',
    preflightConfirm: zh ? '确认并开始回测' : 'Confirm & run',
    preflightCancel: zh ? '返回修改' : 'Go back',
    availWindow: zh ? '实际可用窗口' : 'Available window',
    warmupOkLabel: zh ? '预热充足' : 'Warm-up OK',
    warmupNoLabel: zh ? '预热不足' : 'Warm-up insufficient',
    runAgain: zh ? '重新运行' : 'Run again',
    stStopping: zh ? '平仓归档中' : 'Closing…',
    stStale: zh ? '数据延迟' : 'Stale data',
    stLiquidated: zh ? '已强平' : 'Liquidated',
    stRiskStopped: zh ? '风控熔断' : 'Risk stop',
    stError: zh ? '异常' : 'Error',
    filterAll: zh ? '全部' : 'All',
    filterRunning: zh ? '运行中' : 'Running',
    filterPaused: zh ? '已暂停' : 'Paused',
    filterAbnormal: zh ? '异常' : 'Abnormal',
    filterEnded: zh ? '已结束' : 'Ended',
    lastEval: zh ? '最近评估' : 'Last evaluation',
    nextEval: zh ? '下次检查' : 'Next check',
    noSignalYet: zh ? '尚未触发信号' : 'No signal yet',
    copyCreate: zh ? '复制建立' : 'Copy & create',
    stopConfirmTitle: zh ? '终止模拟盘' : 'Stop paper account',
    stopConfirmMsg: zh ? '将撤销挂单并按市价模拟平仓当前持仓，然后归档。此操作不可撤销。' : 'Open orders will be cancelled and current positions closed at market price before archiving. This cannot be undone.',
    confirm: zh ? '确定' : 'Confirm',
    cancel: zh ? '取消' : 'Cancel',
    pausedHint: zh ? '已暂停新增交易，持仓风险仍会变化' : 'New entries paused; open risk still changes',
    stoppingHint: zh ? '正在撤单并平仓归档，请稍候…' : 'Cancelling orders and closing positions…',
    dataStaleHint: zh ? '行情数据不可用，已暂停新评估；恢复后自动继续' : 'Market data unavailable — evaluation paused; resumes automatically',
    errorHint: zh ? '运行异常，可重试启动' : 'Runtime error — you can retry',
    liquidatedHint: zh ? '已模拟强平，实例结束（只读）' : 'Liquidated (paper) — instance ended',
    riskStoppedHint: zh ? '权益触发风控熔断，实例结束（只读）' : 'Equity hit risk circuit breaker — instance ended',
    clonedFrom: zh ? '复制自' : 'Cloned from',
    decisionLog: zh ? '决策日志' : 'Decision log',
    configSnapshot: zh ? '参数快照' : 'Config snapshot',
    tradeType: zh ? '类型' : 'Type',
    evalTitle: zh ? '证据时间线（每次规则评估）' : 'Evidence timeline (every rule evaluation)',
    evalTime: zh ? '评估时间' : 'Time',
    evalDecision: zh ? '决策' : 'Decision',
    evalReason: zh ? '原因' : 'Reason',
    evalConditions: zh ? '条件' : 'Conditions',
    evalInput: zh ? '输入快照' : 'Input snapshot',
    evalIndicator: zh ? '指标' : 'Indicators',
    loadMore: zh ? '载入更多' : 'Load more',
    noEvaluations: zh ? '暂无评估记录，启动后每根 K 线评估一次' : 'No evaluations yet — one per candle after launch',
    evalChecks: zh ? '检查统计' : 'Check stats',
    dayLabel: zh ? '日期' : 'Day',
    checksLabel: zh ? '检查次数' : 'Checks',
    envTitle: zh ? '规则与市场环境' : 'Rules & market environment',
    envChartTitle: zh ? '价格环境图' : 'Price environment',
    envCurrent: zh ? '当前环境' : 'Current environment',
    envUtc: 'UTC',
    envEr20: 'ER20',
    envStreak: zh ? '已连续确认' : 'consecutive confirmations',
    envDir: zh ? '方向' : 'Direction',
    envVolPct: zh ? '波动分位' : 'Volatility percentile',
    envCaption: zh ? '仅解释当前适用性，实时环境不回填历史' : 'Current conditions only — live regime is not backfilled into history',
    envUnavailable: zh ? '暂无环境数据' : 'Environment data unavailable',
    useTemplate: zh ? '使用此模板' : 'Use this template',
    transparentRules: zh ? '透明规则' : 'Transparent rules',
    ruleEntry: zh ? '入场' : 'Entry',
    ruleExit: zh ? '退出' : 'Exit',
    ruleRisk: zh ? '每笔计划风险' : 'Risk per trade',
    ruleExpand: zh ? '规则可展开查看公式' : 'Expandable to view formulas',
    ruleExpandLabel: zh ? '展开公式' : 'View formula',
    ruleFull: zh ? '规则全文' : 'Full rule',
    risksToKnow: zh ? '需要了解的风险' : 'Risks to know',
    riskFreqStop: zh ? '震荡阶段可能频繁止损' : 'Frequent stop-outs during ranging phases',
    riskGap: zh ? '跳空会超出计划损失' : 'Gaps can exceed planned loss',
    riskNewAlgo: zh ? '新算法尚待完整历史验证' : 'New algorithms await full historical validation',
    // S09 模拟运行详情 + S10 决策证据
    simDetail: zh ? '模拟运行详情' : 'Paper Run Detail',
    evidenceTitle: zh ? '决策证据' : 'Decision Evidence',
    equityUsdt: zh ? '净权益 / USDT' : 'Net equity / USDT',
    unrealizedPnl: zh ? '未实现盈亏' : 'Unrealized PnL',
    realizedPnl: zh ? '已实现盈亏' : 'Realized PnL',
    openPlanRisk: zh ? '开放计划风险' : 'Open plan risk',
    strategySolid: zh ? '策略实线' : 'Strategy solid',
    benchmarkDashed: zh ? '基准虚线' : 'Benchmark dashed',
    marketUpdated: zh ? '行情更新' : 'Market updated',
    pauseNew: zh ? '暂停新增交易' : 'Pause new trades',
    stopAndClose: zh ? '结束并平仓' : 'End & close positions',
    currentFlat: zh ? '当前空仓 · 等待信号' : 'Flat · waiting for signal',
    holdingPos: zh ? '持仓中' : 'In position',
    waitSignal: zh ? '等待环境与突破同时满足' : 'Waiting for regime & breakout to align',
    tabPositions: zh ? '持仓' : 'Positions',
    tabEvents: zh ? '事件' : 'Events',
    tabReview: zh ? '复盘' : 'Review',
    tabBudget: zh ? '预算' : 'Budget',
    tabForwardReplay: zh ? '前向复盘' : 'Forward replay',
    viewEvidence: zh ? '查看证据' : 'View evidence',
    viewTraceEvidence: zh ? '查看逐条证据' : 'View trace evidence',
    viewMetricsEvidence: zh ? '查看指标与原始证据' : 'View metrics & raw evidence',
    noOrderThisTime: zh ? '本次未开仓' : 'No order this time',
    orderThisTime: zh ? '本次已成交' : 'Order placed this time',
    reasonLabel: zh ? '原因' : 'Reason',
    execResult: zh ? '执行结果' : 'Execution result',
    noOrderGenerated: zh ? '未生成订单' : 'No order generated',
    cashUnchanged: zh ? '虚拟现金没有变化' : 'Virtual cash unchanged',
    noFees: zh ? '没有手续费' : 'No fees charged',
    auditTrail: zh ? '可追溯记录' : 'Audit trail',
    snapshotSaved: zh ? '指标值与数据快照已保存' : 'Indicator values & data snapshot saved',
    versionFrozen: zh ? '模板版本不可被覆盖' : 'Template version is immutable',
    expandRaw: zh ? '展开原始栏位' : 'Expand raw fields',
    condName: zh ? '条件' : 'Condition',
    condValue: zh ? '当前值' : 'Current',
    condRequire: zh ? '要求' : 'Requirement',
    condResult: zh ? '结果' : 'Result',
    condPassed: zh ? '通过' : 'Passed',
    condFailed: zh ? '未通过' : 'Failed',
    evidenceNote: zh ? '解释来自真实判断字段，不允许用语言模型编造成交原因。' : 'Explanations come from real decision fields; LLM-made fill-ins are never used for fills.',
    rulesCheck: zh ? '规则检查' : 'Rule checks',
    // S11 研究验证
    researchTitle: zh ? '研究验证' : 'Research Validation',
    researchNoConclusion: zh ? '当前没有该新算法的完整收益结论。研究状态和生产可用状态严格分开。' : 'No complete return conclusion exists for this new algorithm yet. Research status is strictly separate from production availability.',
    researchInProgress: zh ? '研究中' : 'In research',
    moduleRolling: zh ? '滚动验证' : 'Rolling validation',
    moduleHoldout: zh ? '独立测试' : 'Holdout test',
    moduleCost: zh ? '成本压力' : 'Cost stress',
    moduleForward: zh ? '前向观察' : 'Forward observation',
    pendingLabel: zh ? '待完成' : 'Pending',
    notStartedLabel: zh ? '待开始' : 'Not started',
    viewProtocol: zh ? '查看协议和证据' : 'View protocol & evidence',
    publishCheck: zh ? '发布检查' : 'Publish checklist',
    pubSample: zh ? '至少3个完整测试折 / 30笔有效平仓' : 'At least 3 complete test folds / 30 valid closed trades',
    pubDrawdown: zh ? '各折回撤与成本压力达到规定门槛' : 'Drawdown & cost stress meet thresholds across folds',
    pubForward: zh ? '冻结后前向至少90天且达到样本要求' : 'At least 90 days of forward observation after freeze, meeting sample requirements',
    pubGateMobile: zh ? '样本数、回撤、成本同时满足' : 'Sample, drawdown & cost all satisfied',
    pubKeepResearch: zh ? '未通过时保留研究状态' : 'Stays in research status when not met',
    viewAllTrials: zh ? '查看协议与全部试验' : 'View protocol & all trials',
    avoidOverfit: zh ? '避免虚假最优' : 'Avoid false optima',
    avoidCandidates: zh ? '展示所有候选与失败记录' : 'All candidates & failures are shown',
    avoidSelection: zh ? '测试结果不反向参与选参' : 'Test results never feed back into parameter selection',
    avoidNoPromise: zh ? '不承诺未来收益' : 'No future returns are promised',
    adminSubmitOnly: zh ? '仅研究管理员可提交新候选版本，普通用户只读。' : 'Only research admins can submit new candidate versions; read-only for regular users.',
    // S12 启动确认
    startConfirmTitle: zh ? '启动模拟确认' : 'Start Paper Confirmation',
    startConfirmSub: zh ? '启动前核对，确认理解后开始模拟观察。' : 'Review before launch, then confirm to start simulation.',
    preLaunchCheck: zh ? '启动前核对' : 'Pre-launch checklist',
    riskSummary: zh ? '风险摘要' : 'Risk summary',
    perTradeRisk: zh ? '单笔计划风险 {pct}%' : 'Planned risk {pct}% per trade',
    dailyLossPause: zh ? '日亏 2% 暂停新增' : 'Pause new entries on 2% daily loss',
    accountDDEnd: zh ? '账户回撤 10% 结束' : 'Stop at 10% account drawdown',
    researchNotPass: zh ? '研究状态不代表验证通过' : 'Research status is not validation passed',
    virtualOnly: zh ? '仅虚拟模拟' : 'Virtual only',
    virtualOnlyDesc: zh ? '确认理解虚拟资金和研究状态后启动。不会连接交易权限，不会发送真实交易订单。' : 'Start only after acknowledging virtual funds and research status. No trading permissions are connected and no real orders are sent.',
    confirmStartVirtual: zh ? '确认启动虚拟模拟' : 'Confirm start virtual paper',
    iUnderstand: zh ? '确认理解：仅使用虚拟资金；当前版本仍处于研究状态。' : 'I understand: virtual funds only; this version is still in research status.',
    configLocked: zh ? '配置已锁定，可返回修改。' : 'Config is locked; you can go back to adjust.',
    backToConfig: zh ? '返回修改' : 'Back to config',
    dataPreflightOk: zh ? '数据预检通过' : 'Data preflight passed',
    dataPreflightFail: zh ? '数据预检未通过，无法启动' : 'Data preflight failed, cannot start',
    startLockedHint: zh ? '请先勾选确认后再启动' : 'Check the acknowledgement first',
    execMinute: zh ? '执行1m' : 'Exec 1m',
    feeAndSlippage: zh ? '费用 {fee} 与滑点 {slippage}' : 'Fee {fee} & slippage {slippage}',
    configHashLabel: zh ? '当前配置 {hash}，数据预检通过' : 'Current config {hash}, data preflight passed',
    // S13 币池选择
    universeTitle: zh ? '币池选择' : 'Universe Selection',
    universeSub: zh ? '策略实验室 / 透明规则 · 历史验证 · 模拟观察' : 'Strategy Lab / Transparent rules · Historical validation · Simulated observation',
    universeSearchPh: zh ? '搜索名称/代码/交易对' : 'Search name / symbol / pair',
    tabManual: zh ? '手选币池' : 'Manual pool',
    tabDynamic: zh ? '动态币池' : 'Dynamic pool',
    tabSaved: zh ? '已保存币池' : 'Saved pools',
    colMarket: zh ? '市场' : 'Market',
    colCoverage: zh ? '历史覆盖' : 'History coverage',
    colEligible: zh ? '回测资格' : 'Backtest eligibility',
    selectedCount: zh ? '已选择 {n} 个市场' : '{n} markets selected',
    universeTypeStatic: zh ? '币池类型：静态手选' : 'Pool type: static manual',
    universeTypeDynamic: zh ? '币池类型：动态规则' : 'Pool type: dynamic rule',
    lockMembersOnSave: zh ? '保存时锁定成员版本' : 'Member version locked on save',
    checkConfirmUniverse: zh ? '检查并确认币池' : 'Review & confirm pool',
    eligibleYes: zh ? '可回测' : 'Eligible',
    eligibleNo: zh ? '不可选' : 'Not eligible',
    coverageFull: zh ? '完整请求窗口' : 'Full requested window',
    coveragePartial: zh ? '覆盖不足' : 'Partial coverage',
    coverageNone: zh ? '预热不足' : 'Insufficient warm-up',
    eligibilityNote: zh ? '资格由市场、窗口、周期和执行数据共同决定。' : 'Eligibility is determined by market, window, period and execution data.',
    selectedCoin: zh ? '已选择' : 'Selected',
    unselectedCoin: zh ? '未选择' : 'Not selected',
    historyFullEligible: zh ? '历史完整·可回测' : 'History complete · eligible',
    warmupInsufficient: zh ? '预热不足·不可选' : 'Warm-up insufficient · not eligible',
    viewReason: zh ? '查看原因' : 'View reason',
    searchNoResult: zh ? '无匹配市场，试试其他名称或代码' : 'No matching market. Try another name or symbol.',
    confirmSearch: zh ? '确认' : 'Confirm',
    onlyVirtualFunds: zh ? '仅虚拟资金' : 'Virtual funds only',
    universeSavedOk: zh ? '币池已保存并锁定成员版本' : 'Pool saved with member version locked',
    universeDeleteOk: zh ? '已删除' : 'Deleted',
    universeEmpty: zh ? '暂无已保存币池' : 'No saved pools yet',
    savedUniverseName: zh ? '币池名称' : 'Pool name',
    dynamicRuleLabel: zh ? '动态规则' : 'Dynamic rule',
    dynamicRuleDesc: zh ? '按排序字段自动筛选市场，确认时锁定成员快照。' : 'Auto-selects markets by sort field; members are snapshotted on confirm.',
    dynamicTakeN: zh ? '取前 N 个' : 'Top N',
    dynamicSortBy: zh ? '排序字段' : 'Sort by',
    backToStrategy: zh ? '返回策略配置' : 'Back to strategy config',
    // S14 批量回测
    batchResultTitle: zh ? '批量回测结果' : 'Batch Backtest Results',
    batchResultSub: zh ? '策略实验室 / 透明规则 · 历史验证 · 模拟观察' : 'Strategy Lab / Transparent rules · Historical validation · Simulated observation',
    independentBatch: zh ? '独立批量' : 'Independent batch',
    perCoinBudget: zh ? '每币 10,000 USDT' : '10,000 USDT per coin',
    summaryLine: zh ? '{total} 个市场 / {ok} 成功 / {di} 数据不足' : '{total} markets / {ok} succeeded / {di} data insufficient',
    colCoin: zh ? '币种' : 'Coin',
    colNetReturn: zh ? '净收益' : 'Net return',
    colClosedTrades: zh ? '平仓数' : 'Closed trades',
    colState: zh ? '状态' : 'Status',
    stDone: zh ? '完成' : 'Completed',
    stDataInsufficient: zh ? '数据不足' : 'Data insufficient',
    stFailed: zh ? '失败' : 'Failed',
    stPending: zh ? '进行中' : 'Pending',
    completenessTitle: zh ? '任务完整度' : 'Task completeness',
    completenessProgress: zh ? '{ok}/{total} 完成' : '{ok}/{total} completed',
    completenessNote: zh ? '失败币不参与排序；逐币独立虚拟账户；共享组合需另行运行。' : 'Failed coins are excluded from ranking; per-coin virtual accounts; a shared portfolio needs a separate run.',
    failedNotRanked: zh ? '失败币不参与排序' : 'Failed coins are not ranked',
    independentAccounts: zh ? '逐币独立虚拟账户' : 'Per-coin virtual accounts',
    sharedNeedsNewTask: zh ? '共享组合需另行运行' : 'Shared portfolio runs separately',
    viewDistribution: zh ? '查看分布与中位数，不将 {n} 份独立实验相加为一个组合。' : 'View the distribution and median; do not sum {n} independent experiments into one portfolio.',
    mobileSummary: zh ? '{total}币 / 成功 {ok} / 数据不足 {di}' : '{total} coins / {ok} succeeded / {di} data insufficient',
    mobileIncompleteKept: zh ? '未完成结果也保留' : 'Incomplete results are kept',
    incompleteNote: zh ? '{di}币预热数据不足，不纳入成功币指标分母，不能相加为共享资金收益。' : '{di} coins have insufficient warm-up data; excluded from the success denominator and cannot be summed into shared-fund returns.',
    viewPerCoinReports: zh ? '查看逐币报告' : 'View per-coin reports',
    notSumToShared: zh ? '相同窗口与成本' : 'Same window and cost',
    medianReturn: zh ? '中位数收益' : 'Median return',
    sameWindowCost: zh ? '同窗口 · 同成本' : 'Same window · same cost',
    sortNetReturn: zh ? '按净收益排序' : 'Sort by net return',
    sampleResults: zh ? '结果' : 'Results',
    batchQueued: zh ? '批量任务已排队，正在执行…' : 'Batch queued, running…',
    batchRunning: zh ? '批量回测进行中，逐币结果将实时更新' : 'Batch running; per-coin results update live',
    emptyBatchHint: zh ? '暂无批量回测结果' : 'No batch results yet',
    startBatch: zh ? '开始批量回测' : 'Start batch backtest',
    sharedMode: zh ? '共享模式' : 'Shared mode',
    independentMode: zh ? '独立模式' : 'Independent mode',
    sharedBatch: zh ? '共享组合批量' : 'Shared portfolio batch',
    sharedBatchNote: zh ? '共享模式将所选币种放入同一共享资金组合运行（单一共享预算），结果以组合指标与逐币贡献呈现。' : 'Shared mode runs the selected coins in one shared-fund portfolio (single shared budget); results show portfolio metrics and per-coin contribution.',
    portfolioReturn: zh ? '组合收益' : 'Portfolio return',
    portfolioBenchmark: zh ? '基准' : 'Benchmark',
    contributionUsdtHeader: zh ? '贡献/USDT' : 'Contribution/USDT',
    viewPortfolioAttribution: zh ? '查看组合归因' : 'View portfolio attribution',
    sharedResultPending: zh ? '共享组合任务执行中，完成后显示组合指标与逐币贡献' : 'Shared portfolio task running; metrics and per-coin contribution appear when done',
    unitNoteShared: zh ? '逐币贡献以 USDT 计，不可直接相加为组合收益' : 'Per-coin contribution in USDT; do not sum into portfolio return',
    // ---- S15 组合归因 ----
    analysisTitle: zh ? '组合归因' : 'Portfolio Attribution',
    analysisSubtitle: zh ? '策略实验室 / 透明规则 · 历史验证 · 模拟观察' : 'Strategy Lab / transparent rules · historical validation · simulated observation',
    analysisMobileSub: zh ? '策略研究 / 仅虚拟资金' : 'Strategy research / virtual funds only',
    portfolioEquity: zh ? '组合权益' : 'Portfolio equity',
    portfolioMaxDrawdown: zh ? '最大回撤' : 'Max drawdown',
    totalNetPnl: zh ? '总净盈亏' : 'Total net P&L',
    maxHoldings: zh ? '最多持仓' : 'Max holdings',
    portfolioEquityUsdt: zh ? '组合权益/USDT' : 'Portfolio equity/USDT',
    strategySolidBaseDashed: zh ? '策略实线 · 基准虚线' : 'Strategy solid · benchmark dashed',
    sharedRiskLimits: zh ? '共享风险限制' : 'Shared risk limits',
    singleCoinLimit: zh ? '单币上限 {pct}' : 'Per-coin limit {pct}',
    totalExposureLimit: zh ? '总暴露上限 {pct}' : 'Total exposure limit {pct}',
    openRiskLimit: zh ? '开放风险上限 {pct}' : 'Open risk limit {pct}',
    concurrentIntentApproval: zh ? '并发意向统一审批' : 'Concurrent intents approved centrally',
    contributionUsdt: zh ? '贡献/USDT' : 'Contribution/USDT',
    exposure: zh ? '暴露' : 'Exposure',
    holdings: zh ? '持仓' : 'Holdings',
    s15Trades: zh ? '成交' : 'Trades',
    correlation: zh ? '相关性' : 'Correlation',
    totalInclFees: zh ? '合计 {v}（含费用）' : 'Total {v} (incl. fees)',
    contributionSumNotPct: zh ? '各币盈亏以 USDT 计，不可直接相加逐币百分比收益。' : 'Per-coin P&L is in USDT; do not sum per-coin percentage returns.',
    mobileRiskLine: zh ? '单币≤{a} · 总暴露≤{b} · 最多{c}币' : 'Per-coin ≤{a} · exposure ≤{b} · max {c} coins',
    netPnlContribution: zh ? '净盈亏贡献/USDT' : 'Net P&L contribution/USDT',
    unitNote: zh ? '费用与资金费已归属到币种；合计 = 组合净盈亏。' : 'Fees and funding are attributed per coin; total equals portfolio net P&L.',
    // ---- S16 信号分析 ----
    signalAnalysisTitle: zh ? '信号分析' : 'Signal Analysis',
    validIntents: zh ? '有效入场意向' : 'Valid intents',
    executed: zh ? '已执行' : 'Executed',
    rejected: zh ? '被拒绝' : 'Rejected',
    execRate: zh ? '执行率' : 'Execution rate',
    rejectReasonDist: zh ? '拒绝原因分布' : 'Reject reasons',
    filterScope: zh ? '筛选范围' : 'Filter scope',
    filterTemplate: zh ? '模板 / 币种 / 时间' : 'Template / symbol / time',
    signalFilterSymbol: zh ? '币种' : 'Symbol',
    filterTime: zh ? '时间' : 'Time',
    filterReasonStatus: zh ? '拒绝原因 / 执行状态' : 'Reject reason / execution status',
    clickForEvidence: zh ? '点击记录查看详情' : 'Click a record for details',
    signalEvidenceNote: zh ? '决策证据见模拟盘运行详情。' : 'Decision evidence lives in paper run details.',
    ruleNotTriggeredNote: zh ? '算法未触发的评估单独统计，不计有效意向。' : 'Evaluations where rules did not trigger are counted separately and excluded from valid intents.',
    signalMarket: zh ? '市场' : 'Market',
    result: zh ? '结果' : 'Result',
    mainReason: zh ? '主原因' : 'Main reason',
    action: zh ? '操作' : 'Action',
    signalViewEvidence: zh ? '查看证据' : 'View evidence',
    signalViewReport: zh ? '查看报告' : 'View report',
    detailRows: zh ? '逐条查看依据' : 'Intent details',
    clickForDetail: zh ? '点记录查看条件和风险裁剪 →' : 'Click a record to view conditions and risk cuts →',
    chooseTask: zh ? '分析对象' : 'Analysis target',
    chooseTaskHint: zh ? '选择一次回测任务（单币或组合）分析其信号漏斗' : 'Pick a backtest run (single or portfolio) to analyze its signal funnel',
    singleBacktests: zh ? '单币回测' : 'Single backtests',
    sharedBacktests: zh ? '组合回测' : 'Portfolio backtests',
    noTasks: zh ? '暂无回测任务' : 'No backtest tasks',
    noTaskHint: zh ? '先运行一次回测后再来分析' : 'Run a backtest first, then analyze',
    noIntents: zh ? '未触发条件' : 'No conditions triggered',
    noIntentsNote: zh ? '窗口内没有产生有效入场意向；算法评估覆盖见报告。' : 'No valid entry intents within the window; see the report for evaluation coverage.',
    allRejectedNote: zh ? '有效信号全部被拒绝：受风险或预算限制，非策略失效。' : 'All valid signals were rejected due to risk/budget limits, not strategy failure.',
    taskScope: zh ? '模板 / 币种 / 时间 / 成本' : 'Template / symbol / time / cost',
    execState: zh ? '执行状态' : 'Execution state',
    intentDetailTitle: zh ? '意向明细' : 'Intent details',
    reasonNone: zh ? '无' : 'None',
  };
}

/** S15/S16：组合回测任务列表项 */
export interface PortfolioBacktestItem {
  id: string;
  templateKey: string;
  symbols: string[];
  interval: string;
  state: string;
  capital: number;
  startMs: number;
  endMs: number;
  createdAt: string;
  totalReturnPct: number | null;
  maxDrawdownPct: number | null;
  benchmarkReturnPct: number | null;
}

/** S15：组合回测报告（sharedBacktest job.result） */
export interface PortfolioContribution {
  symbol: string;
  pnlContribution: number;
  floatingPnl: number;
  tradeCount: number;
  avgExposurePct: number;
  dataCoveragePct: number;
}
export interface PortfolioReport {
  equityCurve: EquityPoint[];
  benchmarkCurve: EquityPoint[];
  metrics: {
    totalReturnPct: number;
    annualizedPct: number;
    maxDrawdownPct: number;
    winRatePct: number;
    profitFactor: number;
    tradeCount: number;
    finalEquity: number;
    benchmarkReturnPct: number;
    avgExposurePct: number;
    peakExposurePct: number;
    cashRatioPct: number;
    totalCostUsdt: number;
    feesTotalUsdt: number;
    fundingTotalUsdt: number;
    liquidationCount: number;
    liquidationPnlUsdt: number;
  };
  bySymbol: PortfolioContribution[];
  trades: Array<{ symbol: string; time: number; side: 'buy' | 'sell'; price: number; qty: number; pnl: number | null; reason: string }>;
  skips: Array<{ symbol: string; time: number; reason: string }>;
  monthly: Array<{ ym: string; equityEnd: number; retPct: number; benchmarkRetPct: number }>;
  barsUsed: number;
  generatedAt: number;
  limits: { maxPositions: number; singleExposureLimit: number; totalExposureLimit: number; riskFraction: number; openRiskLimit: number };
  capital: number;
  /** P1 风险平衡组合（文档 P77-P80）：子策略贡献与相关性（仅 templateKey='p1' 时存在） */
  subStrategies?: Array<{ id: string; templateKey: 't1' | 'r1'; weight: number; subCapital: number; finalEquity: number; totalReturnPct: number; maxDrawdownPct: number; tradeCount: number }>;
  correlations?: Array<{ a: string; b: string; pearson: number | null; commonDays: number }>;
  /** 期末持仓（合约含方向/名义/强平价；现货含数量） */
  finalPositions?: Array<{
    symbol: string;
    side: 'long' | 'short';
    entryPrice: number;
    markPrice: number;
    notionalUsdt: number;
    floatingPnl: number;
    qty?: number;
    liquidationPrice?: number;
  }>;
  /** P1 币种收益相关性（最近 90 个完整 UTC 日 Pearson；不足 60 共同日 → null 不可估计，文档 P161/P377） */
  correlationMatrix?: {
    symbols: string[];
    days: number;
    values: (number | null)[][];
  };
  /** X2 消融对比：vs 同币池同参数未加过滤 T1 组合（文档 P193） */
  ablation?: {
    filteredReturnPct: number;
    unfilteredReturnPct: number;
    diffPct: number;
    filteredMaxDrawdownPct: number;
    unfilteredMaxDrawdownPct: number;
  };
}

/** S16：意向拒绝原因 → 双语标签（引擎 SKIP_ 与 INSUFFICIENT_ 系列常量） */
export function skipReasonLabel(zh: boolean, reason: string): string {
  const map: Record<string, [string, string]> = {
    SKIP_GAP: ['跳空过大取消', 'Gap too large'],
    SKIP_POSITION_LIMIT: ['持仓数量上限', 'Position count limit'],
    SKIP_TOTAL_EXPOSURE: ['总暴露上限', 'Total exposure limit'],
    SKIP_SYMBOL_LIMIT: ['单币名义上限', 'Per-coin notional limit'],
    SKIP_BUDGET: ['预算不足', 'Insufficient budget'],
    SKIP_OPEN_RISK: ['开放风险上限', 'Open risk limit'],
    SKIP_SYMBOL_OCCUPIED: ['同币已有仓位', 'Symbol already held'],
    SKIP_SYMBOL_LIMIT_EXCEEDED: ['币种数量上限', 'Symbol count limit'],
    SKIP_CAPACITY: ['容量限制', 'Capacity limit'],
    SKIP_DATA: ['执行数据异常', 'Execution data anomaly'],
    SKIP_ENV: ['环境不满足', 'Environment not met'],
    INSUFFICIENT_CASH: ['预算不足', 'Insufficient budget'],
    INSUFFICIENT_MARGIN: ['保证金不足', 'Insufficient margin'],
    DATA_STALE: ['执行数据异常', 'Execution data anomaly'],
  };
  const hit = map[reason];
  if (hit) return zh ? hit[0] : hit[1];
  return reason;
}

/** S15：逐币日盈亏序列两两相关性（对角线 1；样本 <2 显示 null 不编造） */
export function correlationOfSeries(a: number[], b: number[]): number | null {
  const n = Math.min(a.length, b.length);
  if (n < 2) return null;
  const sa = a.slice(0, n);
  const sb = b.slice(0, n);
  const ma = sa.reduce((s, v) => s + v, 0) / n;
  const mb = sb.reduce((s, v) => s + v, 0) / n;
  let cov = 0;
  let va = 0;
  let vb = 0;
  for (let i = 0; i < n; i++) {
    const da = sa[i] - ma;
    const db = sb[i] - mb;
    cov += da * db;
    va += da * da;
    vb += db * db;
  }
  if (va === 0 || vb === 0) return null;
  const r = cov / Math.sqrt(va * vb);
  return Math.max(-1, Math.min(1, r));
}

/** S15：从组合 trades 构建逐币日 pnl 序列（日索引 → 累计 pnl，用于相关性） */
export function perSymbolDailyPnl(trades: PortfolioReport['trades'], symbols: string[]): Record<string, number[]> {
  const bySym = new Map<string, Map<string, number>>();
  for (const s of symbols) bySym.set(s, new Map());
  for (const t of trades) {
    const map = bySym.get(t.symbol);
    if (!map || t.pnl == null) continue;
    const d = new Date(t.time).toISOString().slice(0, 10);
    map.set(d, (map.get(d) ?? 0) + t.pnl);
  }
  const days = new Set<string>();
  for (const map of bySym.values()) for (const d of map.keys()) days.add(d);
  const sorted = [...days].sort();
  const out: Record<string, number[]> = {};
  for (const s of symbols) {
    const map = bySym.get(s)!;
    out[s] = sorted.map((d) => map.get(d) ?? 0);
  }
  return out;
}

export const RISK_COLOR: Record<string, string> = {
  low: '#2E9E5B',  mid: '#2E5AAC',
  high: '#D97706',
  max: '#C0392B',
};

export function riskLabel(zh: boolean, risk: string): string {
  const c = strategyCopy(zh);
  switch (risk) {
    case 'low': return c.riskLow;
    case 'mid': return c.riskMid;
    case 'high': return c.riskHigh;
    default: return c.riskMax;
  }
}

/** BUG05: 模板可读名（模拟盘/报告不直接输出 HL_FOLLOW、DCA 等 ID） */
export const TEMPLATE_NAMES: Record<string, { en: string; zhHant: string }> = {
  dca: { en: 'DCA Accumulate', zhHant: '定投累积' },
  grid: { en: 'Spot Grid', zhHant: '现货网格' },
  rsi: { en: 'RSI Rebound', zhHant: 'RSI 反弹' },
  ma_cross: { en: 'MA Trend', zhHant: '均线趋势' },
  breakout: { en: 'Breakout', zhHant: '突破跟随' },
  hl_follow: { en: 'Futures Direction Follower', zhHant: '合约多空跟随' },
  t1: { en: 'T1 Trend Breakout', zhHant: 'T1 趋势突破' },
  r1: { en: 'R1 Range Reversion', zhHant: 'R1 震荡回归' },
  a1: { en: 'A1 Plan Accumulation', zhHant: 'A1 计划累积' },
  t2: { en: 'T2 MA Trend', zhHant: 'T2 均线趋势' },
  t3: { en: 'T3 Trend Pullback', zhHant: 'T3 趋势回踩' },
  t4: { en: 'T4 Squeeze Breakout', zhHant: 'T4 波动收缩突破' },
  r2: { en: 'R2 RSI Reversal', zhHant: 'R2 RSI 反转' },
  r3: { en: 'R3 Z-Score Reversion', zhHant: 'R3 标准化回归' },
  g1: { en: 'G1 Bounded Spot Grid', zhHant: 'G1 有界现货网格' },
  a2: { en: 'A2 Target-Cap Accumulation', zhHant: 'A2 目标市值累积' },
  x1: { en: 'X1 Relative-Strength Rotation', zhHant: 'X1 相对强弱轮动' },
  x2: { en: 'X2 Breadth-Filtered T1', zhHant: 'X2 广度过滤 T1' },
  p1: { en: 'P1 Risk-Balanced Portfolio', zhHant: 'P1 风险平衡组合' },
};

export function templateName(key: string, zh: boolean): string {
  const n = TEMPLATE_NAMES[key];
  if (n) return zh ? n.zhHant : n.en;
  return key.toUpperCase();
}

/* ---------------- STR11 三问模板向导 ---------------- */

export type AdvisorGoal = 'growth' | 'income' | 'balanced';
export type AdvisorLoss = 'low' | 'mid' | 'high';
export type AdvisorMode = 'spot' | 'futures';

export interface AdvisorRequest {
  goal: AdvisorGoal;
  lossTolerance: AdvisorLoss;
  mode: AdvisorMode;
}

export interface AdvisorCandidate {
  templateKey: string;
  name: I18nTxt;
  marketType: 'spot' | 'futures';
  risk: 'low' | 'mid' | 'high';
  type: string;
  score: number;
  matchReason: I18nTxt;
  caution: I18nTxt;
  sample: {
    totalReturn: number | null;
    benchmarkReturn: number | null;
    maxDrawdown: number | null;
    sampleThrough: string | null;
    barsUsed: number | null;
  } | null;
}

export interface AdvisorResult {
  request: AdvisorRequest;
  candidates: AdvisorCandidate[];
  disclaimer: I18nTxt;
}

export const ADVISOR_GOAL_OPTS: Array<{ value: AdvisorGoal; en: string; zhHant: string }> = [
  { value: 'growth', en: 'Grow my capital', zhHant: '追求资产成长' },
  { value: 'balanced', en: 'Balanced', zhHant: '稳健平衡' },
  { value: 'income', en: 'Steady income', zhHant: '追求稳定收益' },
];

export const ADVISOR_LOSS_OPTS: Array<{ value: AdvisorLoss; en: string; zhHant: string }> = [
  { value: 'low', en: 'Little loss OK', zhHant: '只能接受小亏损' },
  { value: 'mid', en: 'Moderate drawdown OK', zhHant: '可接受中等回撤' },
  { value: 'high', en: 'Big swings OK', zhHant: '可接受大幅波动' },
];

export const ADVISOR_MODE_OPTS: Array<{ value: AdvisorMode; en: string; zhHant: string }> = [
  { value: 'spot', en: 'Spot only', zhHant: '只做现货' },
  { value: 'futures', en: 'Futures (leverage)', zhHant: '合约（含杠杆）' },
];

export interface AdvisorCopy {
  title: string;
  subtitle: string;
  stepGoal: string;
  stepLoss: string;
  stepMode: string;
  goalQ: string;
  lossQ: string;
  modeQ: string;
  getResult: string;
  loading: string;
  resultTitle: string;
  matchReason: string;
  caution: string;
  sample: string;
  sampleThrough: string;
  noCandidates: string;
  goConfig: string;
  disclaimerLabel: string;
  back: string;
  reset: string;
}

export function advisorCopy(zh: boolean): AdvisorCopy {
  return {
    title: zh ? 'AI 帮您选策略' : 'Find your strategy',
    subtitle: zh ? '回答三个问题，我们用规则帮您筛出适合的模板（不会自动改参数，也不会自动开模拟盘）。' : 'Answer three questions and we will filter templates by fixed rules (nothing is auto-changed or auto-started).',
    stepGoal: zh ? '1 / 3' : '1 / 3',
    stepLoss: zh ? '2 / 3' : '2 / 3',
    stepMode: zh ? '3 / 3' : '3 / 3',
    goalQ: zh ? '您的目标是什么？' : 'What is your goal?',
    lossQ: zh ? '您能接受多大亏损？' : 'How much drawdown can you accept?',
    modeQ: zh ? '您想做现货还是合约？' : 'Spot or futures?',
    getResult: zh ? '查看推荐' : 'Get recommendations',
    loading: zh ? '计算中…' : 'Loading…',
    resultTitle: zh ? '推荐结果（依规则匹配）' : 'Recommendations (rule-matched)',
    matchReason: zh ? '为什么适合' : 'Why it fits',
    caution: zh ? '注意' : 'Watch out',
    sample: zh ? '近 1 年样本' : '1Y sample',
    sampleThrough: zh ? '截至' : 'through',
    noCandidates: zh ? '暂无符合条件的模板，请调整选项后重试。' : 'No templates match — adjust your answers and retry.',
    goConfig: zh ? '去配置' : 'Configure',
    disclaimerLabel: zh ? '免责' : 'Disclaimer',
    back: zh ? '返回' : 'Back',
    reset: zh ? '重新回答' : 'Start over',
  };
}

export async function runAdvisor(req: AdvisorRequest): Promise<AdvisorResult> {
  return api.post<AdvisorResult>('/strategy/advisor', req);
}

/* ---------------- V2.0 批次3：共享资金组合模拟（S07/S08/S09/S12/S15） ---------------- */

export type SharedPaperState =
  | 'created'
  | 'running'
  | 'paused'
  | 'stopping'
  | 'stopped'
  | 'data_stale'
  | 'risk_stopped'
  | 'error';

export interface SharedPaperAccount {
  id: string;
  templateKey: string;
  symbols: string[];
  interval: string;
  params: Record<string, any>;
  initialCapital: number;
  cash: number;
  maxPositions: number;
  singleExposureLimit: number;
  totalExposureLimit: number;
  state: SharedPaperState;
  stateVersion: number;
  name: string | null;
  configHash: string | null;
  equitySnapshot: EquityPoint[] | null;
  currentEquity: number;
  returnPct: number;
  stopReason: string | null;
  lastEvalAt: number | null;
  nextEvalAt: number | null;
  dataStaleAt: number | null;
  errorMsg: string | null;
  createdAt: string;
}

export interface SharedPaperDetail {
  account: SharedPaperAccount;
  orders: Array<{
    id?: string;
    paperId: string;
    evaluationId: string;
    sequence: number;
    action: 'buy' | 'sell';
    time: number;
    side: string;
    price: number;
    qty: number;
    pnl: number | null;
    fee: number | null;
    type: string;
    status: string;
    reason: string | null;
  }>;
  positions: Array<{ symbol: string; posQty: number; avgCost: number; entryPrice: number; notional: number }>;
}

export interface SharedPaperCreateInput {
  templateKey: string;
  symbols: string[];
  params?: Record<string, any>;
  initialCapital: number;
  idempotencyKey?: string;
  name?: string;
  maxPositions?: number;
  singleExposureLimit?: number;
  totalExposureLimit?: number;
}

export function sharedPaperStateMeta(state: SharedPaperState, zh: boolean) {
  switch (state) {
    case 'running': return { label: zh ? '运行中' : 'Running', color: '#2E9E5B', bg: '#2E9E5B1a' };
    case 'paused': return { label: zh ? '已暂停新增' : 'Paused (no new)', color: '#D97706', bg: '#D977061a' };
    case 'stopping': return { label: zh ? '结束中' : 'Stopping', color: '#D97706', bg: '#D977061a' };
    case 'stopped': return { label: zh ? '已结束' : 'Stopped', color: '#C0392B', bg: '#C0392B1a' };
    case 'data_stale': return { label: zh ? '数据中断' : 'Data stale', color: '#D97706', bg: '#D977061a' };
    case 'risk_stopped': return { label: zh ? '风险熔断' : 'Risk stopped', color: '#C0392B', bg: '#C0392B1a' };
    case 'error': return { label: zh ? '错误' : 'Error', color: '#C0392B', bg: '#C0392B1a' };
    default: return { label: zh ? '待启动' : 'Created', color: '#888', bg: '#8888881a' };
  }
}

export function fmtUsdt(v: number | null | undefined) {
  if (v == null || !isFinite(v)) return '—';
  return v.toLocaleString(undefined, { maximumFractionDigits: 2 }) + ' USDT';
}

export function fmtTs(ts: number | string | null | undefined) {
  if (ts == null) return '—';
  const n = typeof ts === 'string' ? Number(ts) : ts;
  if (!isFinite(n) || n <= 0) return '—';
  const d = new Date(n);
  const p = (n2: number) => String(n2).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}
